import type { Locale } from '../index'

/**
 * Home page copy in all three locales (en / zh-Hant / zh-Hans).
 *
 * zh-Hant (Traditional — Taiwan / HK) and zh-Hans (Simplified — Mainland China)
 * are distinct, idiomatic translations — never mechanically converted from one
 * to the other. Brand names, email/phone, URLs and demo reference codes are
 * left untranslated; their surrounding labels are translated.
 */
export const home: Record<Locale, Record<string, string>> = {
  en: {
    // Head
    'home.head.title': 'Geo4Logistics — Sea and air freight forwarding',
    'home.head.description':
      'Geo4Logistics is an established sea and air freight forwarder with strengths on U.S. and European lanes — ocean, air, rail and road booked and tracked on one platform, with a customer portal and bilingual support.',

    // Hero
    'home.hero.eyebrow': 'Sea · Air · Rail · Road',
    'home.hero.h1.pre': 'Sea and air freight forwarding,',
    'home.hero.h1.em': 'across global trade lanes.',
    'home.hero.lede':
      'Geo4Logistics is an established sea and air freight forwarder with strengths on U.S. and European lanes. We book, document and track ocean and air cargo — with rail and road where they fit — on one platform you and your team can see.',
    'home.hero.cta.quote': 'Get a quote',
    'home.hero.cta.services': 'Our services',
    'home.hero.stat.shipments': 'shipments handled',
    'home.hero.stat.ports': 'ports, Asia & N. America',
    'home.hero.stat.years': 'years forwarding',

    // Hero track widget
    'home.track.title': 'Track a shipment',
    'home.track.sub': 'Enter a B/L, container or Geo4 reference for live status.',
    'home.track.placeholder': 'e.g. G4-43802470',
    'home.track.aria.ref': 'Shipment reference',
    'home.track.aria.submit': 'Track',
    'home.track.mode.ocean': 'Ocean',
    'home.track.mode.air': 'Air',
    'home.track.mode.rail': 'Rail',
    'home.track.mode.road': 'Road',

    // Trust bar
    'home.trust.carriers': 'Booking space with major ocean and air carriers',

    // Why us
    'home.why.eyebrow': 'Why Geo4Logistics',
    'home.why.h2': 'How we work',
    'home.why.lede':
      'We pair experienced coordinators with a platform that keeps your shipments, documents and conversations in one place — so the people moving your cargo and the system tracking it share the same view.',
    'home.why.0.title': 'Shipment visibility',
    'home.why.0.body':
      'Every leg and milestone — ETD, ATD, ETA, ATA and exceptions — updated as carriers and our operators report them.',
    'home.why.1.title': 'Sea and air, plus overland',
    'home.why.1.body':
      'Ocean FCL/LCL and air freight as our core, with rail and road where they fit — planned together to suit your cost and deadline.',
    'home.why.2.title': 'Customs & compliance',
    'home.why.2.body':
      'Documentation, HS classification and clearance handled by specialists who do it every day.',
    'home.why.3.title': 'Customer portal',
    'home.why.3.body':
      'A secure portal for your team — shipments, quotes, invoices and documents in one place.',
    'home.why.4.title': 'Supplier-to-vendor communication',
    'home.why.4.body':
      'Message the team handling each shipment and upload documents against it, all in one thread.',
    'home.why.5.title': 'Bilingual service',
    'home.why.5.body':
      'Service and a portal in English and Chinese, for cross-border trade between Asia and the world.',

    // Services overview
    'home.svc.eyebrow': 'What we move',
    'home.svc.h2': 'Sea and air first, with rail and road',
    'home.svc.lede':
      'From a single pallet of air freight to a full programme of ocean containers, we plan, book and track the journey — and add rail or road where they make sense.',
    'home.svc.learn': 'Learn more',
    'home.svc.ocean.title': 'Ocean freight',
    'home.svc.ocean.sub': 'FCL & LCL worldwide',
    'home.svc.ocean.li.0': 'Full & less-than-container load',
    'home.svc.ocean.li.1': 'Port-to-port & door-to-door',
    'home.svc.ocean.li.2': 'Reefer & special equipment',
    'home.svc.air.title': 'Air freight',
    'home.svc.air.sub': 'Time-critical cargo',
    'home.svc.air.li.0': 'Express & consolidated',
    'home.svc.air.li.1': 'Airport-to-airport & door',
    'home.svc.air.li.2': 'Time-critical & perishables',
    'home.svc.rail.title': 'Rail freight',
    'home.svc.rail.sub': 'Asia–Europe rail',
    'home.svc.rail.li.0': 'China–Europe block trains',
    'home.svc.rail.li.1': 'Lower cost than air',
    'home.svc.rail.li.2': 'Lower carbon than road',
    'home.svc.road.title': 'Road & customs',
    'home.svc.road.sub': 'First & final mile',
    'home.svc.road.li.0': 'Cross-border trucking',
    'home.svc.road.li.1': 'Customs brokerage',
    'home.svc.road.li.2': 'Warehousing & distribution',

    // Industries
    'home.ind.eyebrow': 'Who we serve',
    'home.ind.h2': 'Industries we ship for',
    'home.ind.lede':
      'We handle the cargo profiles common to trade between Asia, the U.S. and Europe.',
    'home.ind.0.title': 'Consumer electronics',
    'home.ind.0.body':
      'Components and finished goods leaving Asia on tight launch and replenishment windows.',
    'home.ind.1.title': 'Semiconductor',
    'home.ind.1.body':
      'High-value, sensitive cargo with the handling, security and documentation it requires.',
    'home.ind.2.title': 'Industrial & machinery',
    'home.ind.2.body': 'Heavy, oversized and project cargo planned across ocean and air.',
    'home.ind.3.title': 'Retail & e-commerce',
    'home.ind.3.body':
      'Seasonal volumes and ongoing replenishment, planned around your sell-through.',
    'home.ind.4.title': 'Automotive',
    'home.ind.4.body':
      'Parts and components moved on schedules that keep production lines supplied.',
    'home.ind.5.title': 'Healthcare & life sciences',
    'home.ind.5.body': 'Temperature-sensitive and time-critical shipments handled with extra care.',

    // How it works
    'home.how.eyebrow': 'How it works',
    'home.how.h2': 'From quote to delivery, in four steps',
    'home.how.0.title': 'Request a quote',
    'home.how.0.body':
      'Tell us origin, destination, mode and cargo. We come back with clear, itemised pricing and transit time.',
    'home.how.1.title': 'Book & document',
    'home.how.1.body':
      'Accept the quote and we handle the booking, paperwork and customs — you upload what we need, once.',
    'home.how.2.title': 'Track in real time',
    'home.how.2.body':
      'Watch every leg in the portal, get exception alerts, and message the team handling your shipment.',
    'home.how.3.title': 'Deliver & invoice',
    'home.how.3.body':
      'Proof of delivery, clear invoicing and analytics you can use to plan the next shipment.',

    // Portal showcase
    'home.portal.eyebrow': 'The customer portal',
    'home.portal.h2': 'Your shipments, in one place',
    'home.portal.0.title': 'Live shipment list',
    'home.portal.0.body': 'Filter by status and mode, search any PO, B/L, container or vessel.',
    'home.portal.1.title': 'Leg-by-leg timeline',
    'home.portal.1.body':
      "See exactly where cargo is and what's next, with planned vs actual dates.",
    'home.portal.2.title': 'Documents & invoices',
    'home.portal.2.body':
      'HBL, packing lists, arrival notices and invoices — downloadable, in one place.',
    'home.portal.3.title': 'Your whole team',
    'home.portal.3.body':
      'Invite colleagues with the right access. Multi-company support for groups.',
    'home.portal.cta': 'Open a portal demo',
    'home.portal.kpi.active': 'Active',
    'home.portal.kpi.transit': 'In transit',
    'home.portal.kpi.arriving': 'Arriving',
    'home.portal.row.0.route': 'Shanghai → Chicago',
    'home.portal.row.1.route': 'Kaohsiung → Chicago',
    'home.portal.row.2.route': 'Shanghai → Chicago',
    'home.portal.badge.delivered': 'Delivered',
    'home.portal.badge.customs': 'Customs',
    'home.portal.badge.transit': 'In transit',

    // Stats band
    'home.stats.0': 'shipments handled',
    'home.stats.1': 'ports across Asia & North America',
    'home.stats.2': 'ocean freight (FCL & LCL)',
    'home.stats.3': 'air freight, expedited',

    // Testimonials
    'home.testi.eyebrow': 'Customer feedback',
    'home.testi.h2': 'What customers tell us',
    'home.testi.0.quote':
      '"We stopped chasing status updates. The portal shows our whole team where every container is, and the coordinators actually reply."',
    'home.testi.0.role': 'Logistics Manager, Fast Rite International',
    'home.testi.1.quote':
      '"Quotes are itemised and fast, and the bilingual support means our Taipei and Chicago offices are finally on the same page."',
    'home.testi.1.role': 'Operations Lead, Northbridge Supply',
    'home.testi.2.quote':
      '"When a vessel was delayed we knew before our customer did, with a revised ETA already in the system. That\'s the whole point."',
    'home.testi.2.role': 'Supply Chain, Meridian Goods',

    // Certifications
    'home.certs.title': 'Memberships & certifications',

    // CTA band
    'home.cta.h2': 'Move your next shipment with us',
    'home.cta.body':
      "Send us the origin, destination and cargo, and we'll reply with itemised pricing and a transit time — usually within 1–2 business days.",
    'home.cta.quote': 'Get a quote',
    'home.cta.track': 'Track a shipment',
  },

  'zh-Hant': {
    // Head
    'home.head.title': 'Geo4Logistics — 海運與空運貨運代理',
    'home.head.description':
      'Geo4Logistics 是成立有年的海空運貨運代理，於美國與歐洲航線尤具優勢 — 海運、空運、鐵路與陸運皆於單一平台完成預訂與追蹤，並提供客戶平台與雙語服務。',

    // Hero
    'home.hero.eyebrow': '海運 · 空運 · 鐵路 · 陸運',
    'home.hero.h1.pre': '海運與空運貨運代理，',
    'home.hero.h1.em': '貫通全球貿易航線。',
    'home.hero.lede':
      'Geo4Logistics 是成立有年的海空運貨運代理，於美國與歐洲航線尤具優勢。我們為海運與空運貨件提供預訂、文件與追蹤服務，並於合適時搭配鐵路與陸運 — 全部集中在您與團隊都能掌握的單一平台上。',
    'home.hero.cta.quote': '取得報價',
    'home.hero.cta.services': '我們的服務',
    'home.hero.stat.shipments': '處理貨件',
    'home.hero.stat.ports': '港口，亞洲與北美',
    'home.hero.stat.years': '貨運承攬年資',

    // Hero track widget
    'home.track.title': '貨態查詢',
    'home.track.sub': '輸入提單號、貨櫃號或 Geo4 參考號，即可查看即時貨態。',
    'home.track.placeholder': '例如 G4-43802470',
    'home.track.aria.ref': '貨件參考號',
    'home.track.aria.submit': '查詢',
    'home.track.mode.ocean': '海運',
    'home.track.mode.air': '空運',
    'home.track.mode.rail': '鐵路',
    'home.track.mode.road': '陸運',

    // Trust bar
    'home.trust.carriers': '與主要海運及航空承運人預訂艙位',

    // Why us
    'home.why.eyebrow': '為何選擇 Geo4Logistics',
    'home.why.h2': '我們的運作方式',
    'home.why.lede':
      '我們將經驗豐富的協調專員與一套將貨件、文件與溝通集中於同一處的平台相結合 — 讓承運您貨物的人員與追蹤貨物的系統共享同一視野。',
    'home.why.0.title': '貨態能見度',
    'home.why.0.body':
      '每一段航程與里程碑 — ETD、ATD、ETA、ATA 及異常狀況 — 都隨承運人與我方操作人員回報而即時更新。',
    'home.why.1.title': '海空為主，兼顧陸路',
    'home.why.1.body':
      '以海運整櫃／併櫃與空運為核心，並於合適時搭配鐵路與陸運 — 統一規劃，兼顧您的成本與期限。',
    'home.why.2.title': '報關與合規',
    'home.why.2.body': '文件作業、HS 稅則分類與通關，皆由每日處理此類事務的專家負責。',
    'home.why.3.title': '客戶平台',
    'home.why.3.body': '為您的團隊提供安全的客戶平台 — 貨件、報價、發票與文件集中於一處。',
    'home.why.4.title': '供應商與廠商溝通',
    'home.why.4.body': '在同一對話串中，與處理每筆貨件的團隊溝通並上傳相關文件。',
    'home.why.5.title': '雙語服務',
    'home.why.5.body': '以英文與中文提供服務與客戶平台，專為亞洲與世界各地的跨境貿易而設。',

    // Services overview
    'home.svc.eyebrow': '我們承運的貨物',
    'home.svc.h2': '海空優先，輔以鐵路與陸運',
    'home.svc.lede':
      '從單一棧板的空運貨物，到整批海運貨櫃的完整方案，我們規劃、預訂並追蹤整段運程 — 並於合適時加入鐵路或陸運。',
    'home.svc.learn': '了解更多',
    'home.svc.ocean.title': '海運',
    'home.svc.ocean.sub': '全球整櫃與併櫃',
    'home.svc.ocean.li.0': '整櫃與併櫃',
    'home.svc.ocean.li.1': '港到港與門到門',
    'home.svc.ocean.li.2': '冷凍櫃與特殊設備',
    'home.svc.air.title': '空運',
    'home.svc.air.sub': '時效性貨物',
    'home.svc.air.li.0': '快遞與併裝',
    'home.svc.air.li.1': '機場到機場與到府',
    'home.svc.air.li.2': '時效性與生鮮易腐貨物',
    'home.svc.rail.title': '鐵路運輸',
    'home.svc.rail.sub': '亞洲–歐洲鐵路',
    'home.svc.rail.li.0': '中歐班列',
    'home.svc.rail.li.1': '成本較空運低',
    'home.svc.rail.li.2': '碳排較陸運低',
    'home.svc.road.title': '陸運與報關',
    'home.svc.road.sub': '首段與末段運輸',
    'home.svc.road.li.0': '跨境貨車運輸',
    'home.svc.road.li.1': '報關代理',
    'home.svc.road.li.2': '倉儲與配送',

    // Industries
    'home.ind.eyebrow': '我們服務的對象',
    'home.ind.h2': '我們承運的產業',
    'home.ind.lede': '我們處理亞洲、美國與歐洲之間貿易常見的各類貨物。',
    'home.ind.0.title': '消費電子',
    'home.ind.0.body': '由亞洲出口的零組件與成品，配合緊湊的上市與補貨時程。',
    'home.ind.1.title': '半導體',
    'home.ind.1.body': '高價值、敏感的貨物，提供其所需的搬運、保安與文件作業。',
    'home.ind.2.title': '工業與機械',
    'home.ind.2.body': '橫跨海運與空運規劃的重型、超大件與專案貨物。',
    'home.ind.3.title': '零售與電商',
    'home.ind.3.body': '依您的銷售情況，規劃季節性貨量與持續補貨。',
    'home.ind.4.title': '汽車',
    'home.ind.4.body': '依能讓生產線持續供料的時程，運送零件與組件。',
    'home.ind.5.title': '醫療與生命科學',
    'home.ind.5.body': '對溫控與時效性貨物格外用心地處理。',

    // How it works
    'home.how.eyebrow': '運作流程',
    'home.how.h2': '從報價到交付，四個步驟',
    'home.how.0.title': '索取報價',
    'home.how.0.body':
      '告訴我們起運地、目的地、運輸模式與貨物，我們回覆清楚、逐項列明的價格與運送時間。',
    'home.how.1.title': '訂艙與文件',
    'home.how.1.body': '接受報價後，由我們處理訂艙、文件與報關 — 您只需一次上傳所需資料。',
    'home.how.2.title': '即時追蹤',
    'home.how.2.body': '在客戶平台上掌握每一段航程、接收異常通知，並與處理您貨件的團隊溝通。',
    'home.how.3.title': '交付與開立發票',
    'home.how.3.body': '提供交付證明、清楚的發票，以及可用於規劃下一批貨件的分析數據。',

    // Portal showcase
    'home.portal.eyebrow': '客戶平台',
    'home.portal.h2': '您的貨件，集中於一處',
    'home.portal.0.title': '即時貨件清單',
    'home.portal.0.body': '依狀態與運輸模式篩選，並可搜尋任何採購單號、提單號、貨櫃號或船名。',
    'home.portal.1.title': '逐段運程時間軸',
    'home.portal.1.body': '清楚掌握貨物所在位置與下一步進度，並比對計畫與實際日期。',
    'home.portal.2.title': '文件與發票',
    'home.portal.2.body': '分提單、裝箱單、到貨通知與發票 — 皆可下載，集中於一處。',
    'home.portal.3.title': '您的整個團隊',
    'home.portal.3.body': '邀請同事並賦予適當權限。亦支援集團的多公司管理。',
    'home.portal.cta': '開啟平台示範',
    'home.portal.kpi.active': '進行中',
    'home.portal.kpi.transit': '運送中',
    'home.portal.kpi.arriving': '即將到達',
    'home.portal.row.0.route': '上海 → 芝加哥',
    'home.portal.row.1.route': '高雄 → 芝加哥',
    'home.portal.row.2.route': '上海 → 芝加哥',
    'home.portal.badge.delivered': '已交付',
    'home.portal.badge.customs': '報關中',
    'home.portal.badge.transit': '運送中',

    // Stats band
    'home.stats.0': '處理貨件',
    'home.stats.1': '亞洲與北美港口',
    'home.stats.2': '海運（整櫃與拼櫃）',
    'home.stats.3': '空運（快遞）',

    // Testimonials
    'home.testi.eyebrow': '客戶回饋',
    'home.testi.h2': '客戶怎麼說',
    'home.testi.0.quote':
      '「我們不必再追問貨態。客戶平台讓整個團隊都看得到每個貨櫃在哪裡，協調專員也真的會回覆。」',
    'home.testi.0.role': '物流經理，Fast Rite International',
    'home.testi.1.quote':
      '「報價逐項列明又快速，加上雙語支援，讓我們台北與芝加哥的辦公室終於能同步資訊。」',
    'home.testi.1.role': '營運主管，Northbridge Supply',
    'home.testi.2.quote':
      '「船期延誤時，我們比客戶更早得知，系統裡也已備好修訂的 ETA。這正是重點所在。」',
    'home.testi.2.role': '供應鏈，Meridian Goods',

    // Certifications
    'home.certs.title': '會員資格與認證',

    // CTA band
    'home.cta.h2': '把您的下一批貨件交給我們',
    'home.cta.body':
      '將起運地、目的地與貨物資訊傳給我們，我們將回覆逐項列明的價格與運送時間 — 通常於一至兩個工作日內。',
    'home.cta.quote': '取得報價',
    'home.cta.track': '貨態查詢',
  },

  'zh-Hans': {
    // Head
    'home.head.title': 'Geo4Logistics — 海运与空运货运代理',
    'home.head.description':
      'Geo4Logistics 是深耕多年的海运与空运货运代理，在美国与欧洲航线尤具优势 — 海运、空运、铁路与公路运输均在同一平台完成订舱与追踪，并提供客户平台与双语服务。',

    // Hero
    'home.hero.eyebrow': '海运 · 空运 · 铁路 · 公路',
    'home.hero.h1.pre': '海运与空运货运代理，',
    'home.hero.h1.em': '贯通全球贸易航线。',
    'home.hero.lede':
      'Geo4Logistics 是深耕多年的海运与空运货运代理，在美国与欧洲航线尤具优势。我们为海运与空运货物提供订舱、单证与追踪服务，并在适合时搭配铁路与公路运输 — 全部集中在您与团队都能掌握的同一平台上。',
    'home.hero.cta.quote': '获取报价',
    'home.hero.cta.services': '我们的服务',
    'home.hero.stat.shipments': '处理货件',
    'home.hero.stat.ports': '港口，亚洲与北美',
    'home.hero.stat.years': '货运承揽年资',

    // Hero track widget
    'home.track.title': '货物追踪',
    'home.track.sub': '输入提单号、集装箱号或 Geo4 参考号，即可查看实时货态。',
    'home.track.placeholder': '例如 G4-43802470',
    'home.track.aria.ref': '货件参考号',
    'home.track.aria.submit': '追踪',
    'home.track.mode.ocean': '海运',
    'home.track.mode.air': '空运',
    'home.track.mode.rail': '铁路',
    'home.track.mode.road': '公路',

    // Trust bar
    'home.trust.carriers': '与主要海运及航空承运人预订舱位',

    // Why us
    'home.why.eyebrow': '为何选择 Geo4Logistics',
    'home.why.h2': '我们的运作方式',
    'home.why.lede':
      '我们将经验丰富的协调专员与一套将货件、单证与沟通集中于同一处的平台相结合 — 让承运您货物的人员与追踪货物的系统共享同一视野。',
    'home.why.0.title': '货态可视化',
    'home.why.0.body':
      '每一段航程与节点 — ETD、ATD、ETA、ATA 及异常状况 — 都随承运人与我方操作人员上报而实时更新。',
    'home.why.1.title': '海空为主，兼顾陆路',
    'home.why.1.body':
      '以海运整箱／拼箱与空运为核心，并在适合时搭配铁路与公路运输 — 统一规划，兼顾您的成本与时限。',
    'home.why.2.title': '报关与合规',
    'home.why.2.body': '单证处理、HS 编码归类与清关，均由每日处理此类事务的专家负责。',
    'home.why.3.title': '客户平台',
    'home.why.3.body': '为您的团队提供安全的客户平台 — 货件、报价、发票与单证集中于一处。',
    'home.why.4.title': '供应商与厂商沟通',
    'home.why.4.body': '在同一对话中，与处理每笔货件的团队沟通并上传相关单证。',
    'home.why.5.title': '双语服务',
    'home.why.5.body': '以英文与中文提供服务与客户平台，专为亚洲与世界各地的跨境贸易而设。',

    // Services overview
    'home.svc.eyebrow': '我们承运的货物',
    'home.svc.h2': '海空优先，辅以铁路与公路',
    'home.svc.lede':
      '从单一托盘的空运货物，到整批海运集装箱的完整方案，我们规划、订舱并追踪整段运程 — 并在适合时加入铁路或公路运输。',
    'home.svc.learn': '了解更多',
    'home.svc.ocean.title': '海运',
    'home.svc.ocean.sub': '全球整箱与拼箱',
    'home.svc.ocean.li.0': '整箱与拼箱',
    'home.svc.ocean.li.1': '港到港与门到门',
    'home.svc.ocean.li.2': '冷藏箱与特种设备',
    'home.svc.air.title': '空运',
    'home.svc.air.sub': '时效性货物',
    'home.svc.air.li.0': '快递与拼装',
    'home.svc.air.li.1': '机场到机场与上门',
    'home.svc.air.li.2': '时效性与生鲜易腐货物',
    'home.svc.rail.title': '铁路运输',
    'home.svc.rail.sub': '亚洲–欧洲铁路',
    'home.svc.rail.li.0': '中欧班列',
    'home.svc.rail.li.1': '成本低于空运',
    'home.svc.rail.li.2': '碳排低于公路',
    'home.svc.road.title': '公路与报关',
    'home.svc.road.sub': '首段与末端运输',
    'home.svc.road.li.0': '跨境卡车运输',
    'home.svc.road.li.1': '报关代理',
    'home.svc.road.li.2': '仓储与配送',

    // Industries
    'home.ind.eyebrow': '我们服务的对象',
    'home.ind.h2': '我们承运的行业',
    'home.ind.lede': '我们处理亚洲、美国与欧洲之间贸易常见的各类货物。',
    'home.ind.0.title': '消费电子',
    'home.ind.0.body': '由亚洲出口的零部件与成品，配合紧凑的上市与补货时间窗。',
    'home.ind.1.title': '半导体',
    'home.ind.1.body': '高价值、敏感的货物，提供其所需的搬运、安保与单证作业。',
    'home.ind.2.title': '工业与机械',
    'home.ind.2.body': '跨海运与空运规划的重型、超大件与项目货物。',
    'home.ind.3.title': '零售与电商',
    'home.ind.3.body': '依您的动销情况，规划季节性货量与持续补货。',
    'home.ind.4.title': '汽车',
    'home.ind.4.body': '依能让生产线持续供料的排期，运送零件与组件。',
    'home.ind.5.title': '医疗与生命科学',
    'home.ind.5.body': '对温控与时效性货物格外用心地处理。',

    // How it works
    'home.how.eyebrow': '运作流程',
    'home.how.h2': '从报价到交付，四个步骤',
    'home.how.0.title': '索取报价',
    'home.how.0.body':
      '告诉我们起运地、目的地、运输方式与货物，我们回复清晰、逐项列明的价格与运输时间。',
    'home.how.1.title': '订舱与单证',
    'home.how.1.body': '接受报价后，由我们处理订舱、单证与报关 — 您只需一次上传所需资料。',
    'home.how.2.title': '实时追踪',
    'home.how.2.body': '在客户平台上掌握每一段航程、接收异常提醒，并与处理您货件的团队沟通。',
    'home.how.3.title': '交付与开票',
    'home.how.3.body': '提供交付凭证、清晰的发票，以及可用于规划下一批货件的分析数据。',

    // Portal showcase
    'home.portal.eyebrow': '客户平台',
    'home.portal.h2': '您的货件，集中于一处',
    'home.portal.0.title': '实时货件列表',
    'home.portal.0.body': '按状态与运输方式筛选，并可搜索任意采购单号、提单号、集装箱号或船名。',
    'home.portal.1.title': '逐段运程时间轴',
    'home.portal.1.body': '清楚掌握货物所在位置与下一步进度，并对比计划与实际日期。',
    'home.portal.2.title': '单证与发票',
    'home.portal.2.body': '分提单、装箱单、到货通知与发票 — 均可下载，集中于一处。',
    'home.portal.3.title': '您的整个团队',
    'home.portal.3.body': '邀请同事并赋予适当权限。亦支持集团的多公司管理。',
    'home.portal.cta': '打开平台演示',
    'home.portal.kpi.active': '进行中',
    'home.portal.kpi.transit': '运输中',
    'home.portal.kpi.arriving': '即将到达',
    'home.portal.row.0.route': '上海 → 芝加哥',
    'home.portal.row.1.route': '高雄 → 芝加哥',
    'home.portal.row.2.route': '上海 → 芝加哥',
    'home.portal.badge.delivered': '已交付',
    'home.portal.badge.customs': '报关中',
    'home.portal.badge.transit': '运输中',

    // Stats band
    'home.stats.0': '处理货件',
    'home.stats.1': '亚洲与北美港口',
    'home.stats.2': '海运（整柜与拼柜）',
    'home.stats.3': '空运（快递）',

    // Testimonials
    'home.testi.eyebrow': '客户反馈',
    'home.testi.h2': '客户怎么说',
    'home.testi.0.quote':
      '“我们不必再追问货态。客户平台让整个团队都能看到每个集装箱在哪里，协调专员也确实会回复。”',
    'home.testi.0.role': '物流经理，Fast Rite International',
    'home.testi.1.quote':
      '“报价逐项列明又快速，加上双语支持，让我们台北与芝加哥的办公室终于能同步信息。”',
    'home.testi.1.role': '运营主管，Northbridge Supply',
    'home.testi.2.quote':
      '“船期延误时，我们比客户更早得知，系统里也已备好修订的 ETA。这正是关键所在。”',
    'home.testi.2.role': '供应链，Meridian Goods',

    // Certifications
    'home.certs.title': '会员资格与认证',

    // CTA band
    'home.cta.h2': '把您的下一批货件交给我们',
    'home.cta.body':
      '将起运地、目的地与货物信息发给我们，我们将回复逐项列明的价格与运输时间 — 通常在一到两个工作日内。',
    'home.cta.quote': '获取报价',
    'home.cta.track': '货物追踪',
  },
}
